import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from '../components/ui/Toast';

interface MutationOptions<TData, TVariables> {
  successMessage?: string | ((data: TData, variables: TVariables) => string);
  errorMessage?: string | ((error: Error) => string);
  invalidateKeys?: unknown[][];
  onSuccess?: (data: TData, variables: TVariables) => void;
  onError?: (error: Error, variables: TVariables) => void;
}

export function useMutationHook<TData = unknown, TVariables = unknown>(
  mutationFn: (variables: TVariables) => Promise<TData>,
  options: MutationOptions<TData, TVariables> = {},
) {
  const queryClient = useQueryClient();

  const mutation = useMutation<TData, Error, TVariables>({
    mutationFn,
    onSuccess: (data, variables) => {
      // 1. Show success message toast
      if (options.successMessage) {
        const msg =
          typeof options.successMessage === 'function'
            ? options.successMessage(data, variables)
            : options.successMessage;
        toast.success(msg);
      }

      // 2. Invalidate requested query keys
      if (options.invalidateKeys) {
        options.invalidateKeys.forEach((key) => {
          queryClient.invalidateQueries({ queryKey: key });
        });
      }

      // 3. Trigger custom callback
      if (options.onSuccess) {
        options.onSuccess(data, variables);
      }
    },
    onError: (err, variables) => {
      // Show error message toast
      const msg =
        typeof options.errorMessage === 'function'
          ? options.errorMessage(err)
          : (options.errorMessage ?? err.message ?? 'An error occurred while processing action');
      toast.error(msg);

      if (options.onError) {
        options.onError(err, variables);
      }
    },
  });

  return {
    mutate: mutation.mutate,
    mutateAsync: mutation.mutateAsync,
    isLoading: mutation.isPending, // In v5 useMutation uses isPending instead of isLoading
    error: mutation.error,
  };
}
